import {
  Component,
  Input,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { LoadSearch } from '@app/loads/models/load-search.model';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppService } from '@app/app.service';
import { LoadSearchFiltersDialogComponent } from '@app/loads/load-search-filters-dialog/load-search-filters-dialog.component';
import { LoadViewPublicDialogComponent } from '@app/loads/load-view-public-dialog/load-view-public-dialog.component';
import { DatePipe } from '@angular/common';
import { CurrencyPipe } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberFilterDialogComponent } from '@app/shared/number-filter-dialog/number-filter-dialog.component';
import { LocationFilterDialogComponent } from '@app/shared/location-filter-dialog/location-filter-dialog.component';
import { DateFilterDialogComponent } from '@app/shared/date-filter-dialog/date-filter-dialog.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { StateFilterDialogComponent } from '@app/shared/state-filter-dialog/state-filter-dialog.component';
import { UserPlan } from '@app/auth/user.model';
import { HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';

interface FilterChip {
  key: string;
  label: string;
  action: () => void;
  removable: boolean;
}
@Component({
  selector: 'app-load-search',
  imports: [
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DatePipe,
    CurrencyPipe,
    CommonModule,
    MatTooltipModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
  ],
  templateUrl: './load-search.component.html',
  styleUrl: './load-search.component.scss',
})
export class LoadSearchComponent implements AfterViewInit {
  searchId: number | null = null;
  
  @Input() search: LoadSearch | null = null;
  @Input() active = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;

  results = new MatTableDataSource<any>([]);

  minSearchRadius = 50;
  maxSearchRadius = 500;

  private autoRefreshEnabled = true;
  private refreshTimer: any;
  private defaultRefreshInterval = 120; // in seconds, will be set from user plan settings
  private refreshInterval: number; // in milliseconds, will be set from user plan settings

  private defaultTimer: ReturnType<typeof setTimeout> | null = null;
  // Delay before setting the search as default after user stops switching tabs. 
  // This is to avoid setting the default when user is actively switching tabs.
  private setDefaultSearchAfter = 10_000; // 10 seconds

  private manualRefreshLimit = 30_000; // 30 seconds
  private lastManualRefreshAt: number | null = null;

  isMobile = window.innerWidth < 768;

  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns = [
    'is_saved',
    'age',
    'pickup_dates',
    'rate',
    'rpm',
    'origin_deadhead_distance',
    'origin_city',
    'origin_state',
    'destination_deadhead_distance',
    'destination_city',
    'destination_state',
    'distance',
    'weight',
    'dimensions',
    'well_required',
    'carrier_name',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
    
    // If the search is not set and it's not mobile, navigate back to the searches list
    if (!this.search && !this.isMobile) {
      this.goBack();
    }
  }

  filterChips: FilterChip[] = [];

  // Origin will added by default
  // Origin radius will be added by default

  allAvailableFilters: { key: string; label: string }[] = [
    { key: 'destination', label: 'Destination' },

    { key: 'destination_states', label: 'Destination States' },
    { key: 'destination_states_exclude', label: 'Exclude Destination States' },

    { key: 'origin_dates', label: 'Pickup Dates' },

    { key: 'min_distance', label: 'Min Distance' },
    { key: 'max_distance', label: 'Max Distance' },

    { key: 'max_weight', label: 'Max Weight' },

    { key: 'max_length', label: 'Max Length' },
    { key: 'max_width', label: 'Max Width' },
    { key: 'max_height', label: 'Max Height' },
    { key: 'max_well_length', label: 'Max Well Length' },

    { key: 'min_rate', label: 'Min Rate' },
    { key: 'min_rpm', label: 'Min RPM' },
  ];

  availableFilters: { key: string; label: string }[] = [];

  userPlan: UserPlan | null = null;

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Setting up auto-refresh interval based on user plan settings
    this.userPlan = this.authService.getUserPlanFromStorage();
    const refreshSeconds = this.userPlan?.search_auto_refresh_interval_seconds ?? this.defaultRefreshInterval;
    this.refreshInterval = refreshSeconds * 1000; // convert to milliseconds

    // Setting max search radius based on user plan settings
    this.maxSearchRadius = this.userPlan?.max_search_radius_miles ?? this.maxSearchRadius;
   }

  ngOnInit(): void {
    if (!this.authService.userCanSearchLoads()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    if (this.search) {
      this.runSearch();
      this.updateFilterUi();
      return;
    }

    // URL by id should only be used on mobile
    if (!this.isMobile) {
      this.goBack();
      return;
    }

    this.searchId = Number(this.route.snapshot.paramMap.get('id'));

    if (!this.searchId) {
      this.appService.errorSnackBar('Search id is missing.');
      return;
    }

    this.getSearch();
  }

  ngAfterViewInit(): void {
    this.results.paginator = this.paginator;
    this.results.sort = this.sort;

    this.results.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {

        case 'is_saved':
          return item.is_saved ? 1 : 0;

        case 'age':
          return new Date(item.refreshed_at).getTime();

        case 'origin_deadhead_distance':
          return item.origin_deadhead_distance ?? 999999;

        case 'destination_deadhead_distance':
          return item.destination_deadhead_distance ?? 999999;

        case 'origin':
          return item.origin_city ?? '';

        case 'destination':
          return item.destination_city ?? '';

        case 'pickup_dates':
          return item.pickup_date_start ?? '';

        default:
          return item[property];
      }
    };
  }

  ngOnChanges(): void {
    if (this.active) {
      this.startDefaultTimer();

      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
      }
    } else {
      this.stopDefaultTimer();
      this.stopAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.stopDefaultTimer();
    this.stopAutoRefresh();
  }

  private startAutoRefresh(): void {
    if (this.refreshTimer) {
      return;
    }

    this.refreshTimer = setInterval(() => {
      if (!this.loading) {
        this.runSearch();
      }
    }, this.refreshInterval);
  }

  private resetAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.stopAutoRefresh();
      this.startAutoRefresh();
    }
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  getSearch(): void {
    if (!this.searchId) {
      return;
    }

    this.appService.get(`loads/searches/${this.searchId}`).subscribe({
      next: (res) => {
        this.search = res.data;
        this.runSearch();
        this.updateFilterUi();
      },
      error: (err) => {
        this.appService.handleError(err);
        this.goBack();
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/loads/searches']);
  }

  updateSearch(): void {
    const dialogRef = this.dialog.open(LoadSearchFiltersDialogComponent, {
      width: '550px',
      maxWidth: '95vw',
      data: { search: this.search },
    });

    dialogRef.afterClosed().subscribe((result: LoadSearch | undefined) => {
      if (!result) return;

      this.search = result;
      this.runSearch();
      this.updateFilterUi();
    });
  }

  runSearch(resultMessage: boolean = false, manual: boolean = false): void {
    if (!this.search?.id) {
      return;
    }

    // Manual refresh rate limit
    if (manual && this.lastManualRefreshAt) {
      const diff = Date.now() - this.lastManualRefreshAt;

      if (diff < this.manualRefreshLimit) {
        const waitSeconds = Math.ceil((this.manualRefreshLimit - diff) / 1000);
        this.appService.infoSnackBar(`You can refresh again in ${waitSeconds} seconds.`,);
        return;
      }
    }

    if (manual) {
      this.lastManualRefreshAt = Date.now();
    }

    this.loading = true;
    this.resetAutoRefresh();

    this.appService
      .get(`loads/searches/${this.search.id}/results`)
      .subscribe({
        next: (res) => {
          const newData = res.data ?? [];
          const changes = this.getResultChanges(newData);

          this.results.data = newData;
          this.results.paginator = this.paginator;
          this.results.sort = this.sort;
          if (res.time < 0.5) {
            // Ensure spinner shows for at least 500ms to prevent flickering
            setTimeout(() => {
              this.loading = false;
            }, 500);
          } else {
            this.loading = false;
          }

          if (resultMessage) {

            if (res.data?.length === 0) {
              this.appService.showSnackBar(
                `No loads found`,
                'warning'
              );
            } else {
              this.appService.showSnackBar(
                `${res.data?.length ?? 0} loads found`,
                'info'
              );
            }
          }

        },
        error: (err) => {
          this.loading = false;
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  private getResultChanges(newData: any[]) {
    const oldIds = new Set(this.results.data.map(load => load.id));
    const newIds = new Set(newData.map(load => load.id));
    return {
      added: [...newIds].filter(id => !oldIds.has(id)).length,
      removed: [...oldIds].filter(id => !newIds.has(id)).length,
    };
  }

  viewLoadDetails(load: any): void {
    this.dialog.open(LoadViewPublicDialogComponent, {
      width: '900px',
      maxWidth: '900px',
      data: {
        load: load,
        toggleSavedLoad: () => {
          this.toggleSavedLoad(load);
        }
      },
    });
  }

  getDimensions(load: any): string {
    const length = this.toFeetAndInches(load.length);
    const width = this.toFeetAndInches(load.width);
    const height = this.toFeetAndInches(load.height);

    const dimensions = [length, width, height]
      .filter((dim) => dim)
      .join(' × ');

    return dimensions;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  toggleSavedLoad(load: any): void {
    const originalValue = load.is_saved;

    load.is_saved = !originalValue;

    this.appService
      .patch(`saved/loads/${load.id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          load.is_saved = res.data.is_saved;
          this.sortFavoritesOnTop();
        },
        error: (err) => {
          load.is_saved = originalValue;
          this.appService.handleError(err);
        },
      });
  }

  isFresh(item: any): boolean {
    if (!item.refreshed_since) {
      return false;
    }

    const [hours, minutes] = item.refreshed_since.split(':').map(Number);

    return hours === 0 && minutes < 5;
  }

  private sortFavoritesOnTop(): void {
    this.results.data = [...this.results.data].sort((a, b) => {
      const savedCompare = Number(b.is_saved) - Number(a.is_saved);

      if (savedCompare !== 0) {
        return savedCompare;
      }

      return new Date(b.refreshed_at).getTime() - new Date(a.refreshed_at).getTime();
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }

  // Set Default Tab Logic
  // Implemeting timer to avoid refreshing default when user
  // is actively switching the tabs.
  private startDefaultTimer(): void {
    this.stopDefaultTimer();

    this.defaultTimer = setTimeout(() => {
      this.defaultTimer = null;
      this.setDefault();
    }, this.setDefaultSearchAfter);
  }

  private stopDefaultTimer(): void {
    if (this.defaultTimer) {
      clearTimeout(this.defaultTimer);
      this.defaultTimer = null;
    }
  }

  private setDefault(): void {

    if (!this.search || this.search.is_default) {
      return;
    }

    const search = this.search;

    this.appService
      .patch(`loads/searches/${search.id}/default`, {})
      .subscribe({
        next: () => {
          search.is_default = true;
        },
        error: (err) => {
          console.error('Failed to set default search:', err);
        },
      });
  }

  getFilterChips(): FilterChip[] {
    if (!this.search) {
      return [];
    }

    const filters = this.search.filters;
    const chips: FilterChip[] = [];

    const add = (
      key: string,
      label: string,
      action: () => void = () => { },
      removable: boolean = true
    ) => {
      chips.push({
        key,
        label,
        action,
        removable,
      });
    };

    if (filters.origin) {
      add(
        'origin',
        `From: ${this.formatLocationLabel(filters.origin)}`,
        () => this.openLocationFilterDialog('origin', 'Origin'),
        false
      );
    }

    if (filters.origin_radius) {
      add(
        'origin_radius',
        `${filters.origin_radius} mi`,
        () => this.openNumberFilterDialog('origin_radius', 'Origin Radius', 50, this.minSearchRadius, this.maxSearchRadius, 'mi'),
        false,

      );
    }

    if (filters.origin_start_date || filters.origin_end_date) {
      add(
        'origin_dates',
        `Pickup: ${this.formatDateChip(filters.origin_start_date, filters.origin_end_date)}`,
        () => this.openDateRangeFilterDialog(
          'origin_start_date',
          'origin_end_date',
          'Pickup Dates'
        ),
        true
      );
    }

    if (filters.destination) {
      add(
        'destination',
        `To: ${this.formatLocationLabel(filters.destination)}`,
        () => this.openLocationFilterDialog('destination', 'Destination'),
        true,
      );
    }

    if (filters.destination_radius) {
      add(
        'destination_radius',
        `${filters.destination_radius} mi`,
        () => this.openNumberFilterDialog('destination_radius', 'Destination Radius', 50, this.minSearchRadius, this.maxSearchRadius, 'mi'),
        true,
      );
    }

    if (filters.destination_states?.length) {
      add(
        'destination_states',
        `States: ${filters.destination_states.join(', ')}`,
        () => this.openStateFilterDialog('destination_states', 'Destination States'),
        true
      );
    }

    if (filters.destination_states_exclude?.length) {
      add(
        'destination_states_exclude',
        `Exclude States: ${filters.destination_states_exclude.join(', ')}`,
        () => this.openStateFilterDialog('destination_states_exclude', 'Exclude Destination States'),
        true
      );
    }

    if (filters.max_weight) {
      add(
        'max_weight',
        `Max Weight: ${filters.max_weight.toLocaleString()} lbs`,
        () => this.openNumberFilterDialog('max_weight', 'Max Weight', 1000, 1000, 200000, 'lbs'),
        true
      );
    }

    if (filters.max_length) {
      add(
        'max_length',
        `Max Length: ${filters.max_length} ft`,
        () => this.openNumberFilterDialog('max_length', 'Max Length', 1, 120, 1000, 'ft'),
        true
      );
    }

    if (filters.max_width) {
      add(
        'max_width',
        `Max Width: ${filters.max_width} ft`,
        () => this.openNumberFilterDialog('max_width', 'Max Width', 1, 1, 1000, 'ft'),
        true
      );
    }

    if (filters.max_height) {
      add(
        'max_height',
        `Max Height: ${filters.max_height} ft`,
        () => this.openNumberFilterDialog('max_height', 'Max Height', 1, 1, 1000, 'ft'),
        true
      );
    }

    if (filters.max_well_length) {
      add(
        'max_well_length',
        `Max Well Length: ${filters.max_well_length} ft`,
        () => this.openNumberFilterDialog('max_well_length', 'Max Well Length', 1, 120, 1000, 'ft'),
        true
      );
    }

    if (filters.min_rate) {
      add(
        'min_rate',
        `Min Rate: $${filters.min_rate.toLocaleString()}`,
        () => this.openNumberFilterDialog('min_rate', 'Min Rate', 500, 0, 50000, '$'),
        true
      );
    }

    if (filters.min_rpm) {
      add(
        'min_rpm',
        `Min RPM: $${filters.min_rpm}`,
        () => this.openNumberFilterDialog('min_rpm', 'Min RPM', 0.25, 0, 1000, '$'),
        true
      );
    }

    return chips;
  }

  removeFilter(key: string): void {
    if (!this.search) {
      return;
    }

    const filters = this.search.filters as any;

    if (key === 'origin_dates') {
      delete filters.origin_start_date;
      delete filters.origin_end_date;
    } else if (key === 'destination' || key === 'destination_radius') {
      delete filters.destination;
      delete filters.destination_location_id;
      delete filters.destination_latitude;
      delete filters.destination_longitude;
      delete filters.destination_radius;
    } else {
      delete filters[key];
    }

    this.updateFilterUi();
    this.updateSearchFilters();
  }

  openNumberFilterDialog(
    key: string,
    label: string,
    step: number,
    min_value: number | null = null,
    max_value: number | null = null,
    suffix: string = '',
    prefix: string = ''
  ): void {

    if (!this.search) {
      return;
    }

    const value = (this.search.filters as any)[key];

    const dialogRef = this.dialog.open(NumberFilterDialogComponent, {
      data: {
        label,
        value,
        step,
        min_value,
        max_value,
        suffix,
        prefix
      },
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value === undefined || !this.search) {
        return;
      }

      (this.search.filters as any)[key] = value;

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  openLocationFilterDialog(
    key: string,
    label: string
  ): void {
    const radiusKey = `${key}_radius`;
    if (!this.search) {
      return;
    }

    const dialogRef = this.dialog.open(LocationFilterDialogComponent, {
      width: '450px',
      data: {
        label,
        value: (this.search.filters as any)[key] ?? '',
        latitude: (this.search.filters as any)[`${key}_latitude`] ?? null,
        longitude: (this.search.filters as any)[`${key}_longitude`] ?? null,
        radius: (this.search.filters as any)[radiusKey] ?? 250,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !this.search) {
        return;
      }

      this.clearDestinationFilters('destination');

      (this.search.filters as any)[key] = result.location;
      (this.search.filters as any)[`${key}_latitude`] = result.latitude;
      (this.search.filters as any)[`${key}_longitude`] = result.longitude;
      (this.search.filters as any)[radiusKey] = result.radius;

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  openDateRangeFilterDialog(
    startKey: string,
    endKey: string,
    label: string
  ): void {
    if (!this.search) {
      return;
    }

    const dialogRef = this.dialog.open(DateFilterDialogComponent, {
      width: '420px',
      data: {
        mode: 'range',
        label,
        startValue: (this.search.filters as any)[startKey] ?? null,
        endValue: (this.search.filters as any)[endKey] ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === undefined) {
        return;
      }

      if (!this.search) {
        return;
      }

      if (result === null) {
        delete (this.search.filters as any)[startKey];
        delete (this.search.filters as any)[endKey];
      } else {
        (this.search.filters as any)[startKey] = result.start;
        (this.search.filters as any)[endKey] = result.end;
      }

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  openStateFilterDialog(
    key: string,
    label: string
  ): void {

    if (!this.search) {
      return;
    }

    const dialogRef = this.dialog.open(StateFilterDialogComponent, {
      width: '420px',
      data: {
        label,
        values: (this.search.filters as any)[key] ?? [],
      },
    });

    dialogRef.afterClosed().subscribe((values: string[] | undefined) => {
      if (values === undefined) return;
      
      if (!this.search) {
        return;
      }

      this.clearDestinationFilters(key);

      if (!values.length) {
        delete (this.search.filters as any)[key];
      } else {
        (this.search.filters as any)[key] = values;
      }

      this.updateFilterUi();
      this.updateSearchFilters();
      this.runSearch(true, true);
    });
  }

  private formatLocationLabel(location: string): string {
    const withoutZip = location.replace(/\s*\b\d{5}\b\s*/g, '').trim();

    const [city = '', state = ''] = withoutZip.split(',');

    const formattedCity = city
      .trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return state
      ? `${formattedCity}, ${state.trim().toUpperCase()}`
      : formattedCity;
  }

  updateSearchFilters(): void {
    if (!this.search) {
      return;
    }

    this.appService
      .patch(`loads/searches/${this.search.id}`, this.search.filters)
      .subscribe({
        next: () => {
          this.runSearch();
        },
        error: (err) => {
          this.appService.handleError(err);
        },
      });
  }

  addFilter(key: string): void {
    switch (key) {
      case 'destination':
        this.openLocationFilterDialog('destination', 'Destination');
        break;

      case 'destination_radius':
        this.openNumberFilterDialog('destination_radius', 'Destination Radius', 100, this.minSearchRadius, this.maxSearchRadius, 'mi');
        break;

      case 'destination_states':
        this.openStateFilterDialog('destination_states', 'Destination States');
        break;

      case 'destination_states_exclude':
        this.openStateFilterDialog('destination_states_exclude', 'Exclude Destination States');
        break;

      case 'origin_dates':
        this.openDateRangeFilterDialog(
          'origin_start_date',
          'origin_end_date',
          'Pickup Dates'
        );
        break;

      case 'max_weight':
        this.openNumberFilterDialog('max_weight', 'Max Weight', 1000, 1000, 200000, 'lbs');
        break;

      case 'min_distance':
        this.openNumberFilterDialog('min_distance', 'Min Distance', 100, 50, 10000, 'mi');
        break;

      case 'max_distance':
        this.openNumberFilterDialog('max_distance', 'Max Distance', 100, 50, 10000, 'mi');
        break;

      case 'max_length':
        this.openNumberFilterDialog('max_length', 'Max Length', 1, 120, 1000, 'in');
        break;

      case 'max_width':
        this.openNumberFilterDialog('max_width', 'Max Width', 1, 1, 1000, 'in');
        break;

      case 'max_height':
        this.openNumberFilterDialog('max_height', 'Max Height', 1, 1, 1000, 'in');
        break;

      case 'max_well_length':
        this.openNumberFilterDialog('max_well_length', 'Max Well Length', 1, 120, 1000, 'in');
        break;

      case 'min_rate':
        this.openNumberFilterDialog('min_rate', 'Min Rate', 500, 0, 50000, '$');
        break;

      case 'min_rpm':
        this.openNumberFilterDialog('min_rpm', 'Min RPM', 0.25, 0, 1000, '$');
        break;
    }
  }

  updateAvailableFilters(): void {

    if (!this.search) {
      return;
    }

    const filters = this.search.filters as any;

    this.availableFilters = this.allAvailableFilters.filter((filter) => {
      if (filter.key === 'origin_dates') {
        return !filters.origin_start_date && !filters.origin_end_date;
      }

      return !(filter.key in filters);
    });
  }

  updateFilterUi(): void {
    this.filterChips = this.getFilterChips();
    this.updateAvailableFilters();
  }

  formatDateChip(
    start?: string | Date | null,
    end?: string | Date | null
  ): string {
    if (!start && !end) {
      return '';
    }

    const startLabel = start ? this.formatShortDate(start) : '';
    const endLabel = end ? this.formatShortDate(end) : '';

    if (startLabel && endLabel && startLabel === endLabel) {
      return startLabel;
    }

    if (startLabel && endLabel) {
      return `${startLabel} - ${endLabel}`;
    }

    return startLabel || endLabel;
  }

  formatShortDate(value: string | Date): string {
    const date = value instanceof Date
      ? value
      : new Date(`${value}T00:00:00`);

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });
  }

  private clearDestinationFilters(except?: string): void {

    if (!this.search) {
      return;
    }

    const filters = this.search.filters as any;

    if (except !== 'destination') {
      delete filters.destination;
      delete filters.destination_location_id;
      delete filters.destination_latitude;
      delete filters.destination_longitude;
      delete filters.destination_radius;
    }

    if (except !== 'destination_states') {
      delete filters.destination_states;
    }

    if (except !== 'destination_states_exclude') {
      delete filters.destination_states_exclude;
    }
  }

  sortBy(active: string, direction: 'asc' | 'desc'): void {
    this.sort.active = active;
    this.sort.direction = direction;
    this.sort.sortChange.emit({
      active,
      direction,
    });
  }

  clearSort(): void {
    this.sort.active = '';
    this.sort.direction = '';
    this.sort.sortChange.emit({
      active: '',
      direction: '',
    });
  }
}